import clsx from "clsx";
import { type HTMLAttributes, JSX } from "react";
import { ProgressSpinner } from "../progressSpinner/ProgressSpinner";
import { loadingOverlay } from "./LoadingOverlay.css";

/** Props for the LoadingOverlay component. */
export interface LoadingOverlayProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "aria-label"
> {
  /**
   * Accessible label announced by screen readers — describes what is
   * loading. Required so the ``role="status"`` region always has a
   * meaningful announcement; the wrapper sets the role implicitly so
   * consumers can't easily forget the label.
   */
  "aria-label": string;
}

/**
 * Translucent overlay with a centered ProgressSpinner. Anchored to the
 * nearest position-set ancestor via ``position: absolute; inset: 0``,
 * and ``pointer-events: all`` blocks clicks on whatever sits beneath.
 *
 * Pair with a meaningful ``aria-label`` so screen readers announce what
 * is loading — the wrapper sets ``role="status"`` automatically.
 */
export function LoadingOverlay({
  className,
  ...props
}: LoadingOverlayProps): JSX.Element {
  return (
    <div role="status" className={clsx(loadingOverlay, className)} {...props}>
      <ProgressSpinner />
    </div>
  );
}
